import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { clearAuthSession, getAuthToken, getMetadata } from '../utils/encryptedStorage';

/**
 * ProtectedRoute component
 * Wraps routes that require authentication
 * Checks if user has valid token in localStorage metadata
 * Redirects to login if not authenticated
 * 
 * Usage:
 * <Route path="/dashboard" element={
 *   <ProtectedRoute>
 *     <DashboardPage />
 *   </ProtectedRoute>
 * } />
 */
const ProtectedRoute = ({ children, allowedRoles }) => {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Get metadata from localStorage (decrypted)
        const metadata = getMetadata();
        
        if (!metadata) {
          setIsAuthenticated(false);
          setIsChecking(false);
          return;
        }

        const token = getAuthToken();

        if (!token) {
          setIsAuthenticated(false);
          setIsChecking(false);
          return;
        }

        // Verify token with backend
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        
        const response = await fetch(`${API_BASE_URL}/auth/verify-token`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setIsAuthenticated(true);
          } else {
            // Token invalid, clear storage
            clearAuthSession();
            setIsAuthenticated(false);
          }
        } else {
          // Token invalid or expired
          clearAuthSession();
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // On network error, check if token exists locally
        // This allows offline-first behavior
        const metadata = getMetadata();
        if (metadata) {
          // Check if token is present (basic validation)
          if (getAuthToken()) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-250">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 dark:text-gray-400 text-sm">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to landing if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/landing" state={{ from: location }} replace />;
  }

  // Check role-based access
  const metadata = getMetadata();
  const roleId = metadata?.role_id;

  // If allowedRoles is specified, enforce it
  if (allowedRoles) {
    if (roleId == null || !allowedRoles.includes(roleId)) {
      // Admin users blocked from platform → send to /admin
      // Platform users blocked from admin → send to /dashboard
      return <Navigate to={roleId === 99 ? '/admin' : '/dashboard'} replace />;
    }
  } else {
    // No allowedRoles = platform route — admin-only users cannot access
    if (roleId === 99) {
      return <Navigate to="/admin" replace />;
    }
  }

  // Render children if authenticated and authorized
  return children;
};

/**
 * PublicRoute component
 * Wraps auth routes (login, signup) that should redirect to dashboard if already logged in
 * 
 * Usage:
 * <Route path="/auth/login" element={
 *   <PublicRoute>
 *     <LoginPage />
 *   </PublicRoute>
 * } />
 */
const PublicRoute = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      try {
        // Use getMetadata to decrypt the encrypted metadata
        const metadata = getMetadata();
        if (metadata && getAuthToken()) {
          // If stale Job Seeker session (role_id=6) on this HR platform,
          // clear it and treat as unauthenticated
          if (metadata.role_id === 6) {
            clearAuthSession();
            // Don't set isAuthenticated — user must re-login as HR
          } else {
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, []);

  // Show loading while checking
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center transition-colors duration-250">
        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Redirect to appropriate page if already authenticated
  if (isAuthenticated) {
    const metadata = getMetadata();
    const cid = metadata?.cid;

    // Admin users go straight to admin panel
    if (metadata?.role_id === 99) {
      return <Navigate to="/admin" replace />;
    }

    if (!cid || cid === '' || cid === null) {
      return <Navigate to="/auth/workspace-setup" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export { ProtectedRoute, PublicRoute };
